import {useState} from "react";
import {Clock, RefreshCw} from "lucide-react";
import {timeZoneRules} from "../app/ensureTimeZoneRules";
import {CHANGED, noticeWords} from "../model/noticeWords";
import type {TimeZoneRules} from "../model/rules";

const GHOST =
  "inline-flex items-center rounded-md px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-900";
const PRIMARY =
  "inline-flex items-center rounded-md bg-gray-900 px-3 py-1.5 text-sm font-semibold text-white hover:bg-gray-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-900";

/**
 * Instead of the app, when the browser's rules are out of date and the bundled ones could not be loaded: drawing
 * the calendar would show times an hour off in the provinces that changed. Reload tries again; Continue anyway
 * opens the app, said by the standing notice.
 */
export function TimeZoneRulesBlock({
  onContinue,
}: {
  readonly onContinue: () => void;
}) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-gray-50 p-6">
      <section
        role="alert"
        aria-labelledby="tz-rules-title"
        className="w-full max-w-md rounded-xl border border-amber-200 bg-white p-6 shadow-lg"
      >
        <h1
          id="tz-rules-title"
          className="m-0 flex items-center gap-2 text-base font-semibold text-gray-900"
        >
          <Clock className="h-4 w-4 text-amber-600" aria-hidden="true" />
          This browser&rsquo;s time zone rules are out of date
        </h1>
        <p className="m-0 mt-3 text-sm leading-snug text-gray-700">
          {CHANGED} This browser would show their times an hour early, and the
          correction could not be loaded. Reload to try again, or update the
          browser.
        </p>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button type="button" className={GHOST} onClick={onContinue}>
            Continue anyway
          </button>
          <button
            type="button"
            className={PRIMARY}
            onClick={() => window.location.reload()}
            autoFocus
          >
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            Reload
          </button>
        </div>
      </section>
    </main>
  );
}

const DISMISSED = "blh.timeZoneRulesNotice.dismissed";

function wasDismissed(): boolean {
  try {
    return window.sessionStorage.getItem(DISMISSED) === "1";
  } catch {
    return false;
  }
}

/**
 * "Update your browser." Mounted once for the whole app (index.jsx, beside the new-build notice): it speaks when
 * the browser's own rules still show some times wrongly — those of the computer's own clock (a `Date`'s local
 * methods follow them), or every changed zone after Continue anyway. Got it hides it for the session.
 */
export function TimeZoneRulesNotice({
  rules = timeZoneRules(),
}: {
  readonly rules?: TimeZoneRules | null;
}) {
  const [dismissed, setDismissed] = useState(wasDismissed);
  const words = noticeWords(rules);
  if (!words || dismissed) return null;
  const gotIt = () => {
    try {
      window.sessionStorage.setItem(DISMISSED, "1");
    } catch {
      // Not remembered: it is hidden for this page only.
    }
    setDismissed(true);
  };
  // A standing notice at the bottom left of the page area, on the banner layer (1200): above the page, below every
  // dialog (as the new-build notice).
  return (
    <div className="fixed bottom-4 left-4 z-[1200] w-[17rem] max-w-[calc(100vw-2rem)] rounded-xl border border-amber-200 bg-white p-3.5 shadow-lg">
      <div role="status" aria-live="polite" aria-atomic="true">
        <p className="m-0 flex items-center gap-1.5 text-[13px] font-semibold leading-snug text-gray-900">
          <Clock className="h-3.5 w-3.5 text-amber-600" aria-hidden="true" />
          Update your browser
        </p>
        <p className="m-0 mt-1 text-[13px] leading-snug text-gray-600">
          {words}
        </p>
      </div>
      <div className="mt-3 flex justify-end">
        <button type="button" className={GHOST} onClick={gotIt}>
          Got it
        </button>
      </div>
    </div>
  );
}
